// FormEditStructuredTemplate.jsx
import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    CircularProgress,
    Box,
    Paper,
    Typography,
    Divider,
    IconButton,
    Grid,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../../Redux/AlertReducer";
import { useDispatch } from "react-redux";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const FormEditStructuredTemplate = ({ data }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [templateData, setTemplateData] = useState({
        name: "",
        subject: "",
        title: "Social Bulk Market",
        sections: [
            {
                title: "",
                description: "",
                image: "",
                imageFile: null,
                button: "",
                link: ""
            }
        ],
        isHtml: true
    });

    useEffect(() => {
        if (data) {
            setTemplateData({
                name: data.name || "",
                subject: data.subject || "",
                title: data.title || "Social Bulk Market",
                sections: data.sections ? data.sections.map(section => ({
                    title: section.title || "",
                    description: section.description || "",
                    image: section.image || "",
                    imageFile: null, // Reset imageFile for existing sections
                    button: section.button || "",
                    link: section.link || ""
                })) : [
                    {
                        title: "",
                        description: "",
                        image: "",
                        imageFile: null,
                        button: "",
                        link: ""
                    }
                ],
                isHtml: data.is_html || true
            });
        }
    }, [data]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTemplateData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddSection = () => {
        setTemplateData(prev => ({
            ...prev,
            sections: [
                ...prev.sections,
                {
                    title: '',
                    description: '',
                    image: '',
                    imageFile: null,
                    button: '',
                    link: ''
                }
            ]
        }));
    };

    const handleRemoveSection = (index) => {
        if (templateData.sections.length <= 1) return;
        setTemplateData(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index)
        }));
    };

    const handleSectionChange = (index, field, value) => {
        setTemplateData(prev => {
            const updatedSections = [...prev.sections];
            updatedSections[index][field] = value;
            return { ...prev, sections: updatedSections };
        });
    };

    const handleImageChange = (index, file) => {
        if (!file) return;

        // Create a preview URL for the image
        const previewUrl = URL.createObjectURL(file);
        handleSectionChange(index, 'image', previewUrl);

        // Store the file object for later upload
        handleSectionChange(index, 'imageFile', file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoadingSubmit(true);

        const formDataToSend = new FormData();

        // Append basic template data
        formDataToSend.append('name', templateData.name);
        formDataToSend.append('subject', templateData.subject);
        formDataToSend.append('title', templateData.title);
        formDataToSend.append('is_html', templateData.isHtml ? 1 : 0);
        formDataToSend.append('type', 'structured');

        // Append sections data
        templateData.sections.forEach((section, index) => {
            formDataToSend.append(`sections[${index}][title]`, section.title);
            formDataToSend.append(`sections[${index}][description]`, section.description);

            if (section.button) {
                formDataToSend.append(`sections[${index}][button]`, section.button);
            }

            if (section.link) {
                formDataToSend.append(`sections[${index}][link]`, section.link);
            }

            // For new images, append the file
            if (section.imageFile) {
                formDataToSend.append(`sections[${index}][image]`, section.imageFile);
            }
            // For existing images, append the URL
            else if (section.image && !section.image.startsWith('blob:')) {
                formDataToSend.append(`sections[${index}][image]`, section.image);
            }
        });

        try {
            const { result, response, message } = await Helper.Post({
                url: `${api_Routes.EmailTemplates.EditWithSections}${data.id}`,
                data: formDataToSend,
                isFormData: true,
            });

            setIsLoadingSubmit(false);
            if (response) {
                dispatch(setSuccess("Structured email template updated successfully!"));
                navigate("/EmailTemplates");
            }
        } catch (error) {
            console.error("An error occurred during form submission:", error);
            setIsLoadingSubmit(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="p-6 max-w-5xl mx-auto">
                <Paper elevation={3} className="p-6">
                    <Box className="space-y-6">
                        <TextField
                            fullWidth
                            label="Template Name"
                            name="name"
                            required
                            value={templateData.name}
                            onChange={handleInputChange}
                            variant="outlined"
                        />

                        <TextField
                            fullWidth
                            label="Email Subject"
                            name="subject"
                            required
                            value={templateData.subject}
                            onChange={handleInputChange}
                            variant="outlined"
                        />

                        <TextField
                            fullWidth
                            label="Email Title/Header"
                            name="title"
                            required
                            value={templateData.title}
                            onChange={handleInputChange}
                            variant="outlined"
                        />

                        <Divider className="my-4" />

                        <Typography variant="h6" className="mb-4">Email Sections</Typography>

                        {templateData.sections.map((section, index) => (
                            <Paper key={index} elevation={2} className="p-4 mb-4 relative">
                                <IconButton
                                    onClick={() => handleRemoveSection(index)}
                                    color="error"
                                    className="absolute top-2 right-2"
                                    disabled={templateData.sections.length <= 1}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Section Title"
                                            value={section.title}
                                            onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                                            variant="outlined"
                                            margin="normal"
                                            required
                                        />

                                        <TextField
                                            fullWidth
                                            label="Description"
                                            value={section.description}
                                            onChange={(e) => handleSectionChange(index, 'description', e.target.value)}
                                            variant="outlined"
                                            margin="normal"
                                            multiline
                                            rows={3}
                                            required
                                        />

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Button Text"
                                                    value={section.button}
                                                    onChange={(e) => handleSectionChange(index, 'button', e.target.value)}
                                                    variant="outlined"
                                                    margin="normal"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Button Link"
                                                    value={section.link}
                                                    onChange={(e) => handleSectionChange(index, 'link', e.target.value)}
                                                    variant="outlined"
                                                    margin="normal"
                                                    placeholder="https://example.com"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            startIcon={<CloudUploadIcon />}
                                            fullWidth
                                            sx={{ mt: 2, mb: 2 }}
                                        >
                                            Upload Image
                                            <VisuallyHiddenInput
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        handleImageChange(index, e.target.files[0]);
                                                    }
                                                }}
                                            />
                                        </Button>

                                        {section.image && (
                                            <div className="mt-4 flex justify-center">
                                                <img
                                                    src={section.image}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '150px',
                                                        maxHeight: '150px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://placehold.co/150x150?text=Image+Error';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleAddSection}
                            className="mb-4"
                        >
                            Add Section
                        </Button>

                        <Divider className="my-4" />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={templateData.isHtml}
                                    onChange={(e) => setTemplateData({...templateData, isHtml: e.target.checked})}
                                />
                            }
                            label="HTML Email"
                        />

                        <div className="flex justify-end space-x-4 pt-4">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                size="large"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoadingSubmit}
                                size="large"
                            >
                                {isLoadingSubmit ? <CircularProgress size={24} /> : 'Update Template'}
                            </Button>
                        </div>
                    </Box>
                </Paper>
            </div>
        </form>
    );
};